import { Component, EventEmitter, OnInit, Output } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Observable, of } from "rxjs";
import { catchError, map } from "rxjs/operators";

import { ValidationConstants } from "src/app/core/core-services/constants/validation.constants";
import { AuthService } from "src/app/core/core-services/services/auth.service";
import { DataService } from "src/app/core/core-services/services/dataservices/data.service";
import { DialogService } from "src/app/core/core-services/services/dialog.service";
import { SharedDataService } from "src/app/core/core-services/services/shared-data.service";
import { AddShortcutComponent } from "src/app/vengreso/shortcuts/add-shortcut/add-shortcut.component";

/** SharedDataService key this panel's draft is written to on every change — same key/shape the retired wizard's step5 used (`step5.component.ts:130`), so cancelling never loses what was typed. */
const SIG_DATA_KEY = "sig_data";

/**
 * The signature FlyCut's own trigger text (issue #521; the retired wizard's
 * step6 already defaulted its trigger to "-sig"). Issue #546 (stakeholder
 * decision, supersedes the #521 "-sig category" behavior): onboarding no
 * longer creates or resolves a category with this same name — the trigger
 * and the category are now fully unrelated, so there is no matching
 * `SIGNATURE_CATEGORY_NAME` constant any more. The FlyCut is filed under the
 * user's existing default category instead (see `resolveDefaultCategoryId()`).
 */
export const SIGNATURE_FLYCUT_SHORTCUT = "-sig";
export const SIGNATURE_FLYCUT_TITLE = "Email Signature";

/** The 6 fields this panel collects — first/last/email required, the rest optional with placeholder guidance. */
export const SIGNATURE_FORM_FIELDS = ["first_name", "last_name", "email", "title", "company_name", "phone_number"] as const;

export interface SignatureFormValue {
  first_name: string;
  last_name: string;
  email: string;
  title?: string | null;
  company_name?: string | null;
  phone_number?: string | null;
}

/**
 * Builds the signature FlyCut's HTML content from the form fields — the
 * streamlined, non-gamified equivalent of the retired wizard's step7
 * (`step7.component.ts`'s `sigText` template), which built the same shape
 * ("Best,\n\n{name}\n{title}\n{company}\n{phone}\nEmail: {email}") as part of
 * a typing exercise this embedded step doesn't need. `<div>` per line matches
 * the `<p>`-\>`<div>` normalization every other FlyCut save already applies
 * (see `step8.component.ts`'s `htmlStringWithPs`/`saveShortcut()`'s
 * equivalent).
 *
 * Issue #546 scope addendum (stakeholder, 2026-08-17): the title/company/phone
 * lines are ALWAYS present now — an unfilled optional field renders as a
 * literal placeholder token at its line position (`[title]`, `[company_name]`,
 * `[phone_number]`) instead of being omitted, so the user sees exactly where
 * to fill in their signature once it lands as a real FlyCut. Required fields
 * (first/last/email) are always real values — the form enforces them before
 * "Try it out" is even clickable.
 *
 * QA finding on PR #548: a whitespace-only optional value (e.g. "   ") must
 * count as unfilled too — `value || placeholder` alone treats it as
 * "filled" (a non-empty string is truthy) and would render a blank line
 * instead of the placeholder token. Each optional field is trimmed first;
 * empty-after-trim falls back to its placeholder, and a real value that DOES
 * render is rendered trimmed too (e.g. "  Vengreso " -> "Vengreso").
 */
export function composeSignatureHtml(value: SignatureFormValue): string {
  const optionalOrPlaceholder = (raw: string | null | undefined, placeholder: string): string => {
    const trimmed = (raw ?? "").trim();
    return trimmed || placeholder;
  };
  const lines = [
    "Best,",
    "",
    `${value.first_name ?? ""} ${value.last_name ?? ""}`.trim(),
    optionalOrPlaceholder(value.title, "[title]"),
    optionalOrPlaceholder(value.company_name, "[company_name]"),
    optionalOrPlaceholder(value.phone_number, "[phone_number]"),
    `Email: ${value.email ?? ""}`,
  ];
  return lines.map((line) => `<div>${line || "&nbsp;"}</div>`).join("");
}

/**
 * Issue #521: the walk-me modal's "Add a FlyCut" panel (step 2) becomes a
 * signature-creation step — an embedded profile form (ported from the
 * retired wizard's step5, `step5.component.ts:37-50`) replacing the old
 * intro video. Extracted into its own component the same way #519 pulled
 * "About You" out (`about-you-step.component.ts`), since this panel owns
 * real fetch/persist/compose logic the video-only panels don't.
 *
 * Unlike every other panel, clicking through here does NOT itself complete
 * `add_flycut_signature` — this only opens the real `AddShortcutComponent`,
 * fully pre-filled; completion fires from THERE, on an actual FlyCut save
 * (see `add-shortcut.component.ts`'s `createShortcut()`), so the checklist
 * row reflects a real signature FlyCut existing, not just a click.
 */
@Component({
  selector: "app-add-flycut-signature-step",
  templateUrl: "./add-flycut-signature-step.component.html",
  styleUrls: ["./add-flycut-signature-step.component.scss"],
  standalone: false,
})
export class AddFlycutSignatureStepComponent implements OnInit {
  /** Emitted once the pre-filled AddShortcutComponent dialog has been opened — WelcomeModalComponent listens to this purely to hide itself (mirrors every other panel's "Try it out!" -> close() call). */
  @Output() readonly tryItOutClicked = new EventEmitter<void>();

  /** Panel class the pre-filled AddShortcutComponent dialog is opened with — lets `resolveDefaultCategoryId()`'s round trip be guarded against a second dialog via `DialogService.hasOpenDialogWithClass()` (QA F-1 on PR #533). */
  static readonly ADD_SHORTCUT_DIALOG_CLASS = "fms-signature-add-shortcut-dialog";

  form: FormGroup;
  loadFailed = false;
  /**
   * QA F-1 on PR #533: `tryItOut()` PUTs the profile and resolves the
   * preselected category (~600ms measured for the equivalent GET when this
   * guard was written) BEFORE the dialog opens and `tryItOutClicked` hides
   * this whole panel — during that window the CTA stayed visible and
   * enabled, so an ordinary second click (not a synthetic dblclick) opened a
   * SECOND `AddShortcutComponent`. Both mounted `<editor id="editor">`, so
   * the second never got `setEditorManager`, and its Save silently threw.
   * Set synchronously the instant the first click is accepted (before any
   * async work starts) so `canTryItOut` disables the button immediately, and
   * re-checked at the top of `tryItOut()` itself as a second, synchronous
   * line of defense against a same-tick double click landing before
   * Angular's next change-detection pass repaints `[disabled]`.
   */
  isSubmitting = false;

  constructor(
    private readonly fb: FormBuilder,
    private readonly dataService: DataService,
    private readonly authService: AuthService,
    private readonly sharedData: SharedDataService,
    private readonly dialog: DialogService
  ) {
    this.form = this.fb.group({
      first_name: [null, [Validators.required, Validators.pattern(ValidationConstants.NON_DIGIT), Validators.maxLength(48)]],
      last_name: [null, [Validators.required, Validators.pattern(ValidationConstants.NON_DIGIT), Validators.maxLength(48)]],
      email: [null, [Validators.required, Validators.email, Validators.maxLength(70)]],
      title: [null, []],
      company_name: [null, []],
      phone_number: [null, [Validators.maxLength(21), Validators.pattern(ValidationConstants.PHONE_NUMBER_REGEX)]],
    });

    // QA F-6 on PR #533: this used to be created INSIDE the GET v1/user
    // callback in applyProfile() — 1-3s after the panel renders and starts
    // accepting typing — so every keystroke typed before that point was
    // never persisted to sig_data at all. Starting it here, at construction,
    // means the very first character typed is captured, before the form has
    // even had a chance to render let alone the network round trip resolve.
    this.form.valueChanges.subscribe((value) => this.sharedData.setAttribute(SIG_DATA_KEY, value));
  }

  ngOnInit(): void {
    // Phase 1 (synchronous, before any network round trip): seed the form
    // from whatever's already sitting in sig_data. Instant — no flicker —
    // and (QA F-6) this is also what makes "a local draft outranks the
    // server" no longer depend on the draft-application code winning a race
    // against a 1-3s GET; it simply runs first.
    const draft = this.sharedData.getAttribute<Record<string, unknown>>(SIG_DATA_KEY) ?? {};
    if (Object.keys(draft).length) {
      this.form.patchValue(draft, { emitEvent: false });
    }

    this.dataService.get({ url: "v1/user", isLoader: false }).subscribe({
      next: (response: any) => this.applyProfile(response?.result ?? null),
      error: () => {
        this.loadFailed = true;
      },
    });
  }

  get canTryItOut(): boolean {
    const { first_name, last_name, email } = this.form.controls;
    return !this.isSubmitting && first_name.valid && last_name.valid && email.valid;
  }

  tryItOut(): void {
    this.form.markAllAsTouched();
    // QA F-1: `!this.canTryItOut` alone already covers `isSubmitting`, but the
    // explicit `hasOpenDialogWithClass` check is a second, independent guard —
    // the same DialogService precedent PR #531 used — against a second
    // AddShortcutComponent ever stacking, regardless of what this component's
    // own flag happens to say.
    if (!this.canTryItOut || this.dialog.hasOpenDialogWithClass(AddFlycutSignatureStepComponent.ADD_SHORTCUT_DIALOG_CLASS)) {
      return;
    }
    this.isSubmitting = true;

    const value = this.form.value as SignatureFormValue;
    this.saveProfile(value);

    const html = composeSignatureHtml(value);
    this.resolveDefaultCategoryId().subscribe({
      next: (categoryId) => {
        this.openAddShortcutDialog(html, categoryId);
        this.tryItOutClicked.emit();
      },
      // resolveDefaultCategoryId() already swallows its own failures (falls
      // back to null via catchError), so this only guards against a truly
      // unexpected throw in the chain — the CTA must never stay disabled forever.
      error: () => {
        this.isSubmitting = false;
      },
    });
  }

  /**
   * QA F-6 on PR #533: this used to unconditionally patchValue() the WHOLE
   * form the instant GET v1/user resolved (~1-3s after the panel starts
   * accepting typing) — measured at human typing speed, "Analytical
   * Engines" became "tical Engines" under the user's own cursor, and a typed
   * email reverted to the account address and reached the PUT stale. Phase 1
   * (ngOnInit, above) now seeds the draft synchronously before this ever
   * runs, so this only ever fills a field that is BOTH pristine (the user
   * has never touched it — real typing always marks a control dirty) AND
   * still genuinely empty. That second check is what keeps phase 1's draft
   * values from being clobbered here too (a draft-seeded field is pristine
   * but non-empty), preserving the existing "draft wins over the server"
   * rule without a race.
   */
  private applyProfile(profile: Record<string, unknown> | null): void {
    for (const field of SIGNATURE_FORM_FIELDS) {
      const control = this.form.controls[field];
      const profileValue = profile?.[field];
      if (control.pristine && !control.value && profileValue) {
        control.setValue(profileValue, { emitEvent: false });
      }
    }
    this.sharedData.setAttribute(SIG_DATA_KEY, this.form.value);
  }

  private saveProfile(value: SignatureFormValue): void {
    // Mirrors step5.component.ts:92 exactly (same field names, same PUT) —
    // the backend observer maps title/company_name/phone_number into
    // user_info and syncs them to HubSpot from there.
    this.dataService.put({ url: "v1/user", isLoader: false, data: value }).subscribe({
      next: (response: any) => {
        if (response?.result) {
          this.authService.updateUserDetail();
        }
      },
    });
  }

  /**
   * Issue #546 (stakeholder decision, supersedes #521's "-sig category"
   * behavior): onboarding must NEVER create a category — it only reads the
   * user's existing categories and preselects one, exactly like every other
   * read-only category-preselect call site in the app (e.g.
   * `add-shortcut.component.ts`'s `categoryList[0].id` fallback). Picks the
   * account's actual default category (`is_default === true`, the same field
   * `settings.component.html` gates row-actions on and flymsg-be's
   * `CreateDefaultShortcutCategory` listener sets at registration — so every
   * account has exactly one); if that flag is ever missing from the payload,
   * falls back to the first root category returned, matching the fallback
   * convention `add-shortcut.component.ts`'s `setCategoryData()` already uses
   * (`categoryList[0].id`). Zero POSTs/writes here — a read failure just
   * leaves the dialog with no preselected category (`openAddShortcutDialog`
   * already treats `null` that way).
   */
  private resolveDefaultCategoryId(): Observable<string | null> {
    return this.dataService.get({ url: "v1/user/shortcut-category", isLoader: false }).pipe(
      map((response: any) => {
        const categories = Array.isArray(response?.result) ? response.result : [];
        const defaultCategory = categories.find((category: any) => category?.is_default === true);
        return defaultCategory?.id ?? categories[0]?.id ?? null;
      }),
      catchError(() => of(null))
    );
  }

  /** Opens the real AddShortcutComponent, fully pre-filled — title, shortcut, HTML content and category — so the user only has to hit Save. `onboarding: true` still fires on that eventual save (add-shortcut.component.ts's createShortcut(), unchanged), keeping the synthetic first-deploy stats. */
  private openAddShortcutDialog(html: string, categoryId: string | null): void {
    const dialogRef = this.dialog.openDialogComponent(
      AddShortcutComponent,
      {
        // QA F-1: tags this dialog so hasOpenDialogWithClass()/closeDialogWithClass()
        // can reason about it — see the guard at the top of tryItOut().
        class: AddFlycutSignatureStepComponent.ADD_SHORTCUT_DIALOG_CLASS,
        self: categoryId ? { categoryId } : {},
        parentPage: "flyboard",
        shortcutData: {
          title: SIGNATURE_FLYCUT_TITLE,
          flyCut: SIGNATURE_FLYCUT_SHORTCUT,
          flyMsg: html,
        },
      },
      "800px",
      // This panel only ever renders inside the onboarding walk-me modal, so
      // the opened dialog always needs the same disableClose the "+" button's
      // own onboarding-aware open already applies (dashboard.addNewShortcut()).
      true
    );
    // Defensive reset: this component instance is normally torn down almost
    // immediately (tryItOutClicked -> WelcomeModalComponent.close() destroys
    // it), so this rarely fires — but if that teardown is ever delayed, the
    // CTA must not stay permanently disabled once the dialog it was guarding
    // against duplicating is gone.
    dialogRef.afterClosed().subscribe(() => {
      this.isSubmitting = false;
    });
  }
}
